<script lang="ts">
  import { link } from 'svelte-spa-router';
  import { GUEST_TRIAL_PRODUCTS, type GuestTrialAccent } from '../../js/guestTrialProducts';

  function accentVar(name: GuestTrialAccent, shade: 'base' | 'soft' | 'ink' = 'base'): string {
    const suffix = shade === 'base' ? '' : `-${shade}`;
    return `var(--accent-${name}${suffix})`;
  }
</script>

<div class="guest-select">
  <header class="guest-select-header">
    <div class="guest-select-title-row">
      <h1 class="guest-select-title">Choose where to start</h1>
      <span class="free-trial-chip">Free trial</span>
    </div>
    <p class="guest-select-sub">
      Pick the product that matches your research challenge. You can try one free — no account needed to begin.
    </p>
  </header>

  <ul class="product-list" role="list">
    {#each GUEST_TRIAL_PRODUCTS as product, index (product.slug)}
      <li class="product-item">
        <a
          use:link
          href={product.href}
          class="product-card"
          style="--card-accent: {accentVar(product.accent)}; --card-accent-soft: {accentVar(product.accent, 'soft')}; --card-accent-ink: {accentVar(product.accent, 'ink')};"
        >
          <div class="product-card-top">
            <span class="product-index" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
            {#if product.badge}
              <span class="product-badge">{product.badge}</span>
            {/if}
          </div>
          <span class="product-icon-chip" aria-hidden="true">
            <i class="{product.icon} icon"></i>
          </span>
          <h2 class="product-name">{product.name}</h2>
          <p class="product-tagline">{product.tagline}</p>
          <p class="product-description">{product.description}</p>
          <span class="product-arrow" aria-hidden="true">
            <i class="angle right icon"></i>
          </span>
        </a>
      </li>
    {/each}
  </ul>
</div>

<style>
  .guest-select {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: var(--space-8) var(--space-6) var(--space-8);
    padding-top: calc(var(--space-8) + var(--space-5) + 20px);
    min-height: 100%;
    box-sizing: border-box;
  }

  .guest-select-header {
    margin-bottom: var(--space-6);
  }

  .guest-select-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    margin-bottom: var(--space-3);
  }

  .guest-select-title {
    margin: 0;
    font-size: clamp(1.75rem, 4vw, var(--text-4xl));
    font-weight: 600;
    color: var(--grey);
    line-height: 1.15;
  }

  .free-trial-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.45rem 1rem;
    font-size: var(--text-sm);
    font-weight: 500;
    letter-spacing: 0.02em;
    color: var(--accent-orange-ink);
    background: var(--accent-orange-soft);
    border: 1px solid color-mix(in srgb, var(--accent-orange) 25%, transparent);
    border-radius: 999px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .guest-select-sub {
    margin: 0;
    color: var(--grey-secondary);
    line-height: 1.6;
  }

  .product-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--space-4);
    align-items: stretch;
  }

  .product-item {
    min-width: 0;
  }

  .product-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 18rem;
    padding: var(--space-5);
    background: var(--surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-sm);
    text-decoration: none;
    color: inherit;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  }

  .product-card:hover {
    border-color: var(--card-accent);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }

  .product-card:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .product-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
    min-height: 1.5rem;
  }

  .product-index {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey-secondary);
    font-variant-numeric: tabular-nums;
  }

  .product-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--accent-orange-ink);
    background: var(--accent-orange-soft);
    border-radius: 999px;
    margin-left: auto;
  }

  .product-icon-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    margin-bottom: var(--space-4);
    border-radius: var(--radius-md);
    background: var(--card-accent-soft);
    color: var(--card-accent-ink);
    flex-shrink: 0;
  }

  .product-name {
    margin: 0 0 var(--space-2);
    font-size: var(--text-lg);
    font-weight: 600;
    color: var(--grey);
    line-height: 1.25;
  }

  .product-tagline {
    margin: 0 0 var(--space-3);
    font-weight: 500;
    font-size: var(--text-sm);
    color: var(--grey);
    line-height: 1.4;
  }

  .product-description {
    margin: 0;
    flex: 1;
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    line-height: 1.55;
  }

  .product-arrow {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: var(--space-4);
    color: var(--grey-secondary);
    transition: color 0.2s ease, transform 0.2s ease;
  }

  .product-card:hover .product-arrow {
    color: var(--card-accent-ink);
    transform: translateX(3px);
  }

  @media (max-width: 1024px) {
    .product-list {
      grid-template-columns: 1fr;
    }

    .product-card {
      min-height: 0;
    }
  }

  @media (max-width: 768px) {
    .guest-select {
      padding: calc(var(--space-6) + var(--space-4) + 20px) var(--space-4) var(--space-6);
    }

    .guest-select-title-row {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-3);
    }
  }
</style>
